import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'
import { Box, CircularProgress, Typography } from '@mui/material'

/**
 * Redirects unauthenticated users to the Cognito Managed Login UI.
 * No Amplify OAuth — just a plain URL redirect.
 *
 * Required env vars:
 *   VITE_COGNITO_DOMAIN    - e.g. https://us-east-1abcdefg.auth.ap-southeast-2.amazoncognito.com
 *   VITE_USER_POOL_CLIENT_ID - Cognito App Client ID
 *   VITE_CALLBACK_URL      - Where Cognito redirects after login
 */
export default function HostedUIAuth() {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuthContext()

  useEffect(() => {
    if (isLoading) return

    if (isAuthenticated) {
      const from = (location.state as { from?: string })?.from || '/dashboard'
      window.location.href = from
      return
    }

    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN
    const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID
    const callbackUrl = import.meta.env.VITE_CALLBACK_URL

    console.log('Cognito env vars:', { cognitoDomain, clientId, callbackUrl })

    if (!cognitoDomain || !clientId || !callbackUrl) {
      console.error('Missing Cognito Hosted UI env vars: VITE_COGNITO_DOMAIN, VITE_USER_POOL_CLIENT_ID, VITE_CALLBACK_URL')
      console.error('Available env vars:', import.meta.env)
      return
    }

    const loginUrl = `${cognitoDomain}/login?client_id=${clientId}&redirect_uri=${encodeURIComponent(callbackUrl)}&response_type=code&scope=aws.cognito.signin.user.admin+email+openid+profile`
    window.location.href = loginUrl
  }, [isAuthenticated, isLoading, location])

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary">
        Redirecting to login...
      </Typography>
    </Box>
  )
}
